

import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, Radio, Input, Button, Segmented, Switch, Tag } from "antd";
import * as XLSX from "xlsx";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { API_CONFIG } from "./config";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/apiFetch";
import Popup from "./Popup"; // Import the Popup component
import InsightsPanel from "./tables/InsightsPanel";
import CardsView from "./tables/CardsView";
import TableView from "./tables/TableView";

const { Search } = Input;

export default function Grid() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("tables.viewMode");
    return saved === "table" || saved === "cards" ? saved : "cards";
  });
  const [cardFilters, setCardFilters] = useState({});
  const [cardSort, setCardSort] = useState("default");
  const [showAllLow, setShowAllLow] = useState(false);
  const [showAllFull, setShowAllFull] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("tables.isDark");
    return saved === null ? true : saved === "true";
  });
  const useDarkStyles = true;

  // Insights toggle state
  const [showInsights, setShowInsights] = useState(true);
  // Insight tile filtering state
  const [insightFilter, setInsightFilter] = useState(null); // { kind: string, payload?: any }
  const [snapshotBeforeInsight, setSnapshotBeforeInsight] = useState(null);
  const samePayload = (a, b) => {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return a === b; }
  };

  // --- Insight Tile Active/Label helpers ---
  const isTileActive = (kind, payload) => {
    if (!insightFilter) return false;
    if (insightFilter.kind !== kind) return false;
    return samePayload(insightFilter.payload, payload);
  };

  const tileFilterLabel = () => {
    if (!insightFilter) return '';
    const { kind, payload } = insightFilter;
    switch (kind) {
      case 'WARD_NA': return 'Ward: N/A';
      case 'ALIVE': return 'Alive only';
      case 'MARRIED': return 'Married only';
      case 'FEMALE': return 'Female only';
      case 'DIRECTORSHIP': return 'Has Directorship: Yes';
      case 'WARD_EQ': return `Ward ${payload?.ward ?? ''}`.trim();
      case 'DUPLICATES': return 'Duplicate ID numbers';
      default: return '';
    }
  };
  // Central handler: apply or toggle an insight filter that affects both table and cards
  const handleInsightTileClick = (kind, payload) => {
    // Toggle off if the same filter is active
    if (insightFilter && insightFilter.kind === kind && samePayload(insightFilter.payload, payload)) {
      if (snapshotBeforeInsight) {
        setFilteredData(snapshotBeforeInsight);
        setTablePagination((p) => ({ ...p, current: 1, total: snapshotBeforeInsight.length }));
      }
      setInsightFilter(null);
      setSnapshotBeforeInsight(null);
      return;
    }

    // Save snapshot of current view to allow toggling off
    if (!snapshotBeforeInsight) setSnapshotBeforeInsight(filteredData);

    // Build predicate based on tile kind
    const dupSet = new Set();
    const counts = {};
    const base = filteredData; // apply on current view to compose with other filters
    const buildDuplicates = () => {
      base.forEach(r => {
        const key = (r.idNumber || '').trim();
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
      });
      Object.entries(counts).forEach(([k, c]) => { if (c > 1) dupSet.add(k); });
    };

    const isWardMatch = (r, w) => {
      const rw = r.wardNumber ?? r.ward;
      if (w === 'N/A') return (!r.wardNumber && !r.ward) || r.wardNumber === 'N/A' || r.ward === 'N/A';
      return rw === w || String(rw) === String(w);
    };

    let predicate = () => true;
    switch (kind) {
      case 'CLEAR_ALL':
        setFilteredData(tableData);
        setTablePagination((p) => ({ ...p, current: 1, total: tableData.length }));
        setInsightFilter(null);
        setSnapshotBeforeInsight(null);
        return;
      case 'WARD_NA':
        predicate = (r) => (!r.wardNumber && !r.ward) || r.wardNumber === 'N/A' || r.ward === 'N/A';
        break;
      case 'ALIVE':
        predicate = (r) => (r.deceasedStatus || r.aliveStatus) === 'Alive';
        break;
      case 'MARRIED':
        predicate = (r) => String(r.maritalStatus || '').toUpperCase() === 'MARRIED';
        break;
      case 'FEMALE':
        predicate = (r) => r.gender === 'Female';
        break;
      case 'DIRECTORSHIP':
        predicate = (r) => getHasDirectorship(r) === 'Yes';
        break;
      case 'WARD_EQ':
        predicate = (r) => isWardMatch(r, payload?.ward);
        break;
      case 'DUPLICATES':
        buildDuplicates();
        predicate = (r) => dupSet.has((r.idNumber || '').trim());
        break;
      default:
        // No-op: keep current filteredData
        predicate = () => true;
    }

    const next = base.filter(predicate);
    setFilteredData(next);
    setTablePagination((p) => ({ ...p, current: 1, total: next.length }));
    setInsightFilter({ kind, payload });
  };
  // --- INSIGHTS METRICS ---
  // Derive wardGroups: group by ward and compute notMatchedPercent, deceasedPercent
  const wardGroups = useMemo(() => {
    const groups = {};
    filteredData.forEach(r => {
      const ward = r.wardNumber || r.ward || 'N/A';
      if (!groups[ward]) groups[ward] = { ward, total: 0, notMatched: 0, deceased: 0 };
      groups[ward].total++;
      if (r.idnoMatchStatus === 'Not Matched' || r.matchStatus === 'Not Matched') groups[ward].notMatched++;
      if (r.deceasedStatus === 'Deceased' || r.aliveStatus === 'Deceased') groups[ward].deceased++;
    });
    return Object.values(groups).map(g => ({
      ward: g.ward,
      notMatchedPercent: g.total ? ((g.notMatched / g.total) * 100) : 0,
      deceasedPercent: g.total ? ((g.deceased / g.total) * 100) : 0,
      total: g.total
    }));
  }, [filteredData]);

  // Compute insights metrics
  const totalRecords = filteredData.length;
  const uniqueWards = new Set(filteredData.map(r => r.wardNumber || r.ward)).size;
  const wardNAPercent = totalRecords ? ((filteredData.filter(r => !r.wardNumber && !r.ward || r.wardNumber === 'N/A' || r.ward === 'N/A').length / totalRecords) * 100).toFixed(1) : "0.0";
  const matchedPercent = totalRecords ? ((filteredData.filter(r => (r.idnoMatchStatus || r.matchStatus) === 'Matched').length / totalRecords) * 100).toFixed(1) : "0.0";
  const notMatchedPercent = (100 - parseFloat(matchedPercent)).toFixed(1);
  const alivePercent = totalRecords ? ((filteredData.filter(r => (r.deceasedStatus || r.aliveStatus) === 'Alive').length / totalRecords) * 100).toFixed(1) : "0.0";
  const deceasedPercent = totalRecords ? ((filteredData.filter(r => (r.deceasedStatus || r.aliveStatus) === 'Deceased').length / totalRecords) * 100).toFixed(1) : "0.0";
  const marriedPercent = totalRecords ? ((filteredData.filter(r => (r.maritalStatus || '').toUpperCase() === 'MARRIED').length / totalRecords) * 100).toFixed(1) : "0.0";
  const femalePercent = totalRecords ? ((filteredData.filter(r => r.gender === 'Female').length / totalRecords) * 100).toFixed(1) : "0.0";
  const missingIDs = filteredData.filter(r => !r.idNumber).length;
  const duplicateIDs = totalRecords - new Set(filteredData.map(r => r.idNumber)).size;
  const topNotMatched = [...wardGroups].sort((a,b)=>b.notMatchedPercent-a.notMatchedPercent).slice(0,3);
  const topDeceased = [...wardGroups].sort((a,b)=>b.deceasedPercent-a.deceasedPercent).slice(0,3);

  // --- Table pagination state ---
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // --- Table Filters State (moved inside component) ---
  const [tableFiltersState, setTableFiltersState] = useState({});

  // Helper: check if there are any table filters
  const hasTableFilters = Object.values(tableFiltersState || {}).some(
    (v) => Array.isArray(v) && v.length
  );

  // Helper: clear all table filters
  const clearTableFilters = () => setTableFiltersState({});

  // Helper: render active filter chips for the table
  const renderTableFilterChips = () => {
    const filterMeta = {
      wardNumber: { label: "Ward", format: (val) => `Ward ${val}` },
      idnoMatchStatus: { label: "DHA" },
      deceasedStatus: { label: "Deceased" },
      maritalStatus: { label: "Marital" },
      gender: { label: "Gender" },
      hasDirectorship: { label: "Directorship" },
    };
    const chips = [];
    for (const [key, vals] of Object.entries(tableFiltersState || {})) {
      if (!Array.isArray(vals) || !vals.length) continue;
      const meta = filterMeta[key] || {};
      vals.forEach((val) => {
        chips.push(
          <span
            key={key + String(val)}
            className="status-chip"
            style={{
              borderRadius: 999,
              color: "var(--lux-white)",
              background: "rgba(253,224,71,0.13)",
              border: "1px solid rgba(253,224,71,0.4)",
            }}
          >
            {meta.label ? `${meta.label}: ` : ""}
            {meta.format ? meta.format(val) : val}
          </span>
        );
      });
    }
    return chips;
  };
  // Helper: normalise "has directorship" value (now infers from company data too)
  const getHasDirectorship = (item) => {
    // 1) Primary explicit fields (various schemas)
    const raw =
      item?.hasDirectorship ??
      item?.has_directorship ??
      item?.hasDirectorshipFlag ??
      item?.hasDirectorships ??
      item?.directorship ??
      item?.has_director_ship;

    // If explicit boolean/string-like, honour it
    if (typeof raw === "boolean") return raw ? "Yes" : "No";
    if (typeof raw === "string") {
      const s = raw.trim().toLowerCase();
      if (["yes","y","true","1"].includes(s)) return "Yes";
      if (["no","n","false","0"].includes(s)) return "No";
      // fallthrough to infer from related fields
    }

    // 2) Infer from related company / business connection fields
    const hasCompanyText = (v) => typeof v === "string" && v.trim().length > 0;
    const hasArr = (v) => Array.isArray(v) && v.length > 0;

    const inferredYes =
      hasCompanyText(item?.companyName) ||
      hasCompanyText(item?.company) ||
      hasCompanyText(item?.employerName) ||
      hasCompanyText(item?.companyRegNumber) ||
      hasCompanyText(item?.companyRegistrationNumber) ||
      hasCompanyText(item?.CommercialName1) ||
      hasArr(item?.companyNames) ||
      hasArr(item?.businessConnections) ||
      hasArr(item?.BusinessConnections) ||
      hasArr(item?.directorships) ||
      hasArr(item?.directorShips) ||
      (typeof item?.businessConnectionCount === "number" && item.businessConnectionCount > 0) ||
      // Nested shapes
      (item?.company && (hasCompanyText(item.company?.name) || hasArr(item.company?.names))) ||
      (item?.employment && (hasCompanyText(item.employment?.employer) || hasArr(item.employment?.directorships)));

    if (inferredYes) return "Yes";

    // 3) Default to No if nothing else signals Yes
    if (raw == null) return "No";
    return "No";
  };

  const navigate = useNavigate();
  const { user } = useAuth();

  const formData = {
    username: user.username,
    file: "Tables.js" // returns "Tables.js"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiFetch(API_CONFIG.verificationUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Ocp-Apim-Subscription-Key": API_CONFIG.apiKey,
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        // Add _ord property to each item based on index
        const withOrd = data.rtv.map((item, idx) => ({ ...item, _ord: idx }));
        setTableData(withOrd);
        setFilteredData(withOrd);
        setTablePagination((prev) => ({ ...prev, total: withOrd.length }));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    localStorage.setItem("tables.viewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("tables.isDark", String(isDark));
  }, [isDark]);

  const handleGenderFilter = (gender) => {
    if (gender === "All") {
      setFilteredData(tableData);
      setTablePagination((p) => ({ ...p, current: 1, total: tableData.length }));
    } else {
      setFilteredData(tableData.filter((item) => item.gender === gender));
      setTablePagination((p) => ({
        ...p,
        current: 1,
        total: tableData.filter((item) => item.gender === gender).length,
      }));
    }
  };

  const handleSearch = (value) => {
    const searchValue = value ? value.toLowerCase() : "";
    const filtered = tableData.filter((item) => {
      const idNumber = item.idNumber ? item.idNumber.toLowerCase() : "";
      const fullName = `${item.firstNames ?? ""} ${item.surName ?? ""}`.toLowerCase();
      const wardNumber = item.wardNumber ? `ward ${item.wardNumber}`.toLowerCase() : "";
      return (
        idNumber.includes(searchValue) ||
        fullName.includes(searchValue) ||
        wardNumber.includes(searchValue)
      );
    });
    setFilteredData(filtered);
    setTablePagination((p) => ({ ...p, current: 1, total: filtered.length }));
  };

  const handleRowClick = (record) => {
    setSelectedPerson(record);
    setIsModalVisible(true); // Open the modal
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleAddClick = () => {
    navigate("/search");
  };

  // Export to Excel handler
  const handleExportToExcel = (dataOverride) => {
    const source = Array.isArray(dataOverride) ? dataOverride : filteredData;
    if (!source || source.length === 0) return;
    const exportData = source.map((item) => ({
      Ward: item.wardNumber ? `Ward ${item.wardNumber}` : "N/A",
      "ID Number": item.idNumber,
      "Full Name": `${item.firstNames || ""} ${item.surName || ""}`.trim(),
      "DHA Status": item.idnoMatchStatus,
      "Deceased Status": item.deceasedStatus,
      "Marital Status": item.maritalStatus,
      Gender: item.gender,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Indigent Data");
    const dateStr = new Date().toISOString().slice(0,10);
    XLSX.writeFile(wb, `indigent_data_${dateStr}.xlsx`);
  };

  const columns = [
    {
      title: "Ward",
      dataIndex: "wardNumber",
      key: "wardNumber",
      filters: Array.from({ length: 100 }, (_, i) => ({
        text: `Ward ${i + 1}`,
        value: i + 1,
      })), // Dynamically generate filters for wards 1–100
      onFilter: (value, record) => record.wardNumber === value,
      render: (wardNumber) => (wardNumber ? `Ward ${wardNumber}` : "N/A"),
      sorter: (a, b) => a.wardNumber - b.wardNumber,
      filteredValue: tableFiltersState.wardNumber || null,
    },
    {
      title: "ID Number",
      dataIndex: "idNumber",
      key: "idNumber",
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text, record) =>
        `${record.firstNames || ""} ${record.surName || ""}`.trim(),
      sorter: (a, b) => a.firstNames.localeCompare(b.firstNames),
    },
    {
      title: "DHA Status",
      dataIndex: "idnoMatchStatus",
      key: "idnoMatchStatus",
      filters: [
        { text: "Matched", value: "Matched" },
        { text: "Not Matched", value: "Not Matched" },
      ],
      onFilter: (value, record) => record.idnoMatchStatus === value,
      render: (idnoMatchStatus) => (
        <Tag color={idnoMatchStatus === "Matched" ? "green" : "red"}>
          {idnoMatchStatus}
        </Tag>
      ),
      filteredValue: tableFiltersState.idnoMatchStatus || null,
    },
    {
      title: "Deceased Status",
      dataIndex: "deceasedStatus",
      key: "deceasedStatus",
      filters: [
        { text: "Alive", value: "Alive" },
        { text: "Deceased", value: "Deceased" },
      ],
      onFilter: (value, record) => record.deceasedStatus === value,
      render: (deceasedStatus) => (
        <Tag color={deceasedStatus === "Alive" ? "green" : "red"}>
          {deceasedStatus}
        </Tag>
      ),
      filteredValue: tableFiltersState.deceasedStatus || null,
    },
    {
      title: "Marital Status",
      dataIndex: "maritalStatus",
      key: "maritalStatus",
      filters: [
        { text: "MARRIED", value: "MARRIED" },
        { text: "SINGLE", value: "SINGLE" },
        { text: "DIVORCED", value: "DIVORCED" },
        { text: "WIDOWED", value: "WIDOWED" },
      ],
      onFilter: (value, record) => record.maritalStatus === value,
      filteredValue: tableFiltersState.maritalStatus || null,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      filters: [
        { text: "Male", value: "Male" },
        { text: "Female", value: "Female" },
      ],
      onFilter: (value, record) => record.gender === value,
      filteredValue: tableFiltersState.gender || null,
    },
    {
      title: "Has Directorship",
      dataIndex: "hasDirectorship",
      key: "hasDirectorship",
      filters: [
        { text: "Yes", value: "Yes" },
        { text: "No", value: "No" },
      ],
      onFilter: (value, record) => getHasDirectorship(record) === value,
      render: (_val, record) => {
        const v = getHasDirectorship(record);
        return <Tag color={v === "Yes" ? "gold" : "default"}>{v}</Tag>;
      },
      filteredValue: tableFiltersState.hasDirectorship || null,
    },
  ];

  const onChange = (e) => {
    handleGenderFilter(e.target.value);
  };

  // --- Card filter chip helpers ---
  const toggleCardFilter = (category, value) => {
    setCardFilters((prev) => {
      const current = prev[category] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [category]: exists ? current.filter(v => v !== value) : [...current, value],
      };
    });
  };

  const clearAllCardFilters = () => setCardFilters({});

  const cardFilteredData = useMemo(() => {
    let data = [...filteredData];
    Object.keys(cardFilters).forEach(cat => {
      if (cardFilters[cat]?.length) {
        data = data.filter(item => {
          if (cat === "hasDirectorship") {
            return cardFilters[cat].includes(getHasDirectorship(item));
          }
          return cardFilters[cat].includes(item[cat]);
        });
      }
    });
    return data;
  }, [filteredData, cardFilters]);

  const cardSortedData = useMemo(() => {
    let sorted = [...cardFilteredData];
    if (cardSort === "default") {
      sorted = [...cardFilteredData].sort((a, b) => (a._ord ?? 0) - (b._ord ?? 0));
    } else {
      switch (cardSort) {
        case "name": {
          const full = (x) => (`${x.firstNames || ""} ${x.surName || ""}`).trim().toLowerCase();
          sorted.sort((a, b) => full(a).localeCompare(full(b)));
          break;
        }
        case "ward": {
          const ward = (x) => (typeof x.wardNumber === "number" ? x.wardNumber : Number.POSITIVE_INFINITY);
          sorted.sort((a, b) => ward(a) - ward(b));
          break;
        }
        case "dha": {
          const order = { Matched: 0, "Not Matched": 1 };
          const val = (x) => (order[x.idnoMatchStatus] ?? 9);
          sorted.sort((a, b) => val(a) - val(b));
          break;
        }
        case "deceased": {
          const order = { Alive: 0, Deceased: 1 };
          const val = (x) => (order[x.deceasedStatus] ?? 9);
          sorted.sort((a, b) => val(a) - val(b));
          break;
        }
        default:
          break;
      }
    }
    return sorted;
  }, [cardFilteredData, cardSort]);

  // Table onChange handler to update filter state and pagination
  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableFiltersState(filters);
    setTablePagination((prev) => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || prev.pageSize,
    }));
  };

  // Keep tablePagination.total in sync with filteredData
  useEffect(() => {
    setTablePagination((p) => ({ ...p, total: filteredData.length }));
  }, [filteredData]);

  // --- Quality Scores calculation (top 5 wards by weighted score) ---
  const computeQualityScores = (data) => {
    const grouped = {};
    data.forEach(item => {
      const ward = item.wardNumber || 'N/A';
      if (!grouped[ward]) grouped[ward] = { total: 0, matched: 0, alive: 0 };
      grouped[ward].total++;
      if (item.idnoMatchStatus === 'Matched') grouped[ward].matched++;
      if (item.deceasedStatus === 'Alive') grouped[ward].alive++;
    });
    return Object.entries(grouped).map(([ward, stats]) => {
      const matchRate = stats.total ? (stats.matched / stats.total) : 0;
      const aliveRate = stats.total ? (stats.alive / stats.total) : 0;
      const score = (matchRate * 0.7 + aliveRate * 0.3) * 100;
      return { ward, score: Math.round(score), matchRate: Math.round(matchRate*100), aliveRate: Math.round(aliveRate*100) };
    }).sort((a,b) => b.score - a.score);
  };
  const allScores = computeQualityScores(filteredData);
  const topScores = allScores.slice(0,5);
  const bottomScores = allScores.slice(-5).reverse();
  const lowAll = allScores.filter((x) => x.score < 100).sort((a,b) => a.score - b.score);
  const fullAll = allScores.filter((x) => x.score === 100);

  // --- KPI aggregates (auto-sync with filters) ---
  const totalRows = filteredData.length || 0;
  const wardNaCount = filteredData.filter(x => !x.wardNumber).length;
  const wardNaPct = totalRows ? Math.round((wardNaCount / totalRows) * 100) : 0;

  const genderMaleCount = filteredData.filter(x => x.gender === 'Male').length;
  const genderFemaleCount = filteredData.filter(x => x.gender === 'Female').length;
  const genderMalePct = totalRows ? Math.round((genderMaleCount / totalRows) * 100) : 0;
  const genderFemalePct = totalRows ? Math.round((genderFemaleCount / totalRows) * 100) : 0;

  const aliveCount = filteredData.filter(x => x.deceasedStatus === 'Alive').length;
  const deceasedCount = filteredData.filter(x => x.deceasedStatus === 'Deceased').length;
  const alivePct = totalRows ? Math.round((aliveCount / totalRows) * 100) : 0;
  const deceasedPct = totalRows ? Math.round((deceasedCount / totalRows) * 100) : 0;

  const marriedCount = filteredData.filter(x => x.maritalStatus === 'MARRIED').length;
  const marriedPct = totalRows ? Math.round((marriedCount / totalRows) * 100) : 0;

  // Has Directorship KPI: count records with CommercialName1
  const hasDirectorshipYes = filteredData.filter(item => item.CommercialName1).length;

  // Ward with the most number of indigents (by count in filteredData)
  const wardCounts = filteredData.reduce((acc, r) => {
    const w = r.wardNumber || r.ward || 'N/A';
    acc[w] = (acc[w] || 0) + 1;
    return acc;
  }, {});
  const mostIndigentWardEntry = Object.entries(wardCounts).sort((a, b) => b[1] - a[1])[0] || ['N/A', 0];
  const mostIndigentWard = mostIndigentWardEntry[0];
  const mostIndigentWardCount = mostIndigentWardEntry[1];
  const mostIndigentWardPct = totalRecords ? ((mostIndigentWardCount / totalRecords) * 100) : 0;

  // Ward with the least number of indigents (by count in filteredData)
  const leastIndigentWardEntry = Object.entries(wardCounts).sort((a, b) => a[1] - b[1])[0] || ['N/A', 0];
  const leastIndigentWard = leastIndigentWardEntry[0];
  const leastIndigentWardCount = leastIndigentWardEntry[1];
  const leastIndigentWardPct = totalRecords ? ((leastIndigentWardCount / totalRecords) * 100) : 0;

  // --- Game-theory inspired metrics ---
  // 1) Verification Game Index (VGI)
  const _matched = parseFloat(matchedPercent) || 0;
  const _alive = parseFloat(alivePercent) || 0;
  const _wardNA = parseFloat(wardNAPercent) || 0;
  const VGI = Math.round(0.6 * _matched + 0.3 * _alive + 0.1 * (100 - _wardNA));

  // 2) Dominant Strategy Ward (Next Best Move)
  // Unverified = Not Matched OR Missing ID
  const unverifiedByWard = filteredData.reduce((acc, r) => {
    const w = r.wardNumber || r.ward || 'N/A';
    const notMatched = (r.idnoMatchStatus === 'Not Matched' || r.matchStatus === 'Not Matched');
    const missingID = !r.idNumber;
    const unv = (notMatched || missingID) ? 1 : 0;
    acc[w] = (acc[w] || 0) + unv;
    return acc;
  }, {});
  const nextBestArray = Object.entries(unverifiedByWard).map(([w,u]) => ({
    ward: w,
    unverified: u,
    total: wardCounts[w] || 0,
    score: u * (u / Math.max(wardCounts[w] || 0, 1)) // quadratic emphasis on concentrated unverified
  }));
  const nextBest = nextBestArray.sort((a,b) => b.score - a.score)[0] || { ward: 'N/A', unverified: 0, total: 0, score: 0 };
  const nextBestPctInWard = nextBest.total ? Math.round((nextBest.unverified / nextBest.total) * 100) : 0;

  // 3) Prisoner's Dilemma Alert (Duplicates Pressure)
  const pdAlertPct = totalRecords ? Math.round((duplicateIDs / totalRecords) * 100) : 0;

  // 4) Directorship Exposure Index
  const directorshipExposurePct = totalRecords ? Math.round((hasDirectorshipYes / totalRecords) * 100) : 0;

  // 5) Equity Balance (Gender Mix Fairness)
  const equityBalance = Math.max(0, Math.min(100, Math.round(100 - Math.abs((parseFloat(femalePercent)||0) - 50) * 2)));

  const leaderboardProps = {
    topScores,
    allScores,
    bottomScores,
    lowAll,
    fullAll,
    showAllLow,
    setShowAllLow,
    showAllFull,
    setShowAllFull,
  };

  return (
    <>
      <style jsx global>{`
        .luxe-light .indigent-card{
          background: radial-gradient(800px 160px at -10% -20%, rgba(253,224,71,0.18), rgba(253,224,71,0.0)), #fff;
          border: 1px solid rgba(212,175,55,.55);
          box-shadow: 0 10px 22px rgba(0,0,0,.10), inset 0 1px 0 rgba(255,255,255,0.7);
        }
        .luxe-light .indigent-name{ color:#111; }
        .luxe-light .indigent-meta{ color:#475569; }
        .luxe-light .indigent-line{ color:#374151; }
        .luxe-light .indigent-avatar{ box-shadow: 0 4px 10px rgba(212,175,55,.35); }
        .luxe-light .chip{ color:#111; }
        .luxe-light .chip.dark{
          color:#111;
          border-color: rgba(212,175,55,.55);
          background: linear-gradient(180deg, rgba(253,224,71,.25), rgba(253,224,71,.08));
        }
        .luxe-light .chip-filter{
          color:#111;
          border-color: rgba(212,175,55,.55);
          background: rgba(253,224,71,0.18);
        }
        .luxe-light .chip-filter.active{
          color:#111;
          background: linear-gradient(180deg, rgba(253,224,71,0.45), rgba(253,224,71,0.18));
          border-color:#d4af37;
          box-shadow:0 0 8px rgba(212,175,55,0.35);
        }
        .luxe-light .chip-filter.clear{
          color:#b91c1c;
          border-color:#fca5a5;
          background: rgba(248,113,113,0.15);
        }
        .luxe-light .status-chip{
          color:#111;
          border: 1px solid rgba(212,175,55,.55);
          background: rgba(253,224,71,0.15);
        }
        .luxe-light .status-chip.clear{
          color:#b91c1c;
          border-color:#fca5a5;
          background: rgba(248,113,113,0.15);
        }
        .luxe-light .luxe-table .ant-table-thead > tr > th{
          background:#fff7d6 !important;
          color:#111 !important;
          border-bottom:2px solid #d4af37;
        }
        .luxe-light .luxe-table .ant-table-tbody > tr > td{
          background:#fff !important;
          color:#111 !important;
          border-bottom:1px solid rgba(0,0,0,0.06);
        }
        .luxe-light .luxe-table .ant-table-row:hover > td{
          background:#fff1a8 !important;
          color:#111 !important;
        }
        /* Light mode: force tiles + sort/filter chips to match dark luxe */
        .luxe-light .kpi-card{
          background: #0f1117 !important;
          color: #f5f5f5 !important;
          border: 1px solid rgba(212,175,55,0.45) !important;
          box-shadow: 0 6px 18px rgba(0,0,0,.28) !important;
        }
        .luxe-light .kpi-title,
        .luxe-light .kpi-value{ color:#f5f5f5 !important; }
        .luxe-light .chip-filter{
          color:#f5f5f5;
          border-color: rgba(212,175,55,.45);
          background: #14141b;
        }
        .luxe-light .chip-filter.active{
          color:#fff8dc;
          background: linear-gradient(180deg, rgba(253,224,71,0.25), rgba(253,224,71,0.10));
          border-color:#d4af37;
          box-shadow:0 0 8px rgba(212,175,55,0.4);
        }
        .luxe-light .chip-filter.clear{
          color:#ff6666;
          border-color:#ff6666;
          background: rgba(255,102,102,0.08);
        }
        .luxe-light .status-chip{
          color:#f5f5f5;
          border: 1px solid rgba(212,175,55,.45);
          background: #14141b;
        }
        .luxe-light .status-chip.clear{
          color:#ff6666;
          border-color:#ff6666;
          background: rgba(255,102,102,0.08);
        }
        .podium-grid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(220px,1fr)); gap:12px; }
        .medal-card{ position:relative; overflow:hidden; border:1px solid rgba(212,175,55,0.45) !important; }
        .medal-card::after{ content:''; position:absolute; inset:0; background: radial-gradient(400px 80px at -10% -10%, rgba(253,224,71,0.10), rgba(253,224,71,0.0)); pointer-events:none; }
        .medal-rank{ font-size:24px; }
        .medal-ward{ font-weight:800; font-size:16px; margin-top:4px; }
        .medal-score{ font-weight:800; font-size:28px; line-height:1; margin:4px 0; }
        .medal-meta{ font-size:12px; }
        .ranked-list{ display:flex; flex-direction:column; gap:8px; margin-top:12px; }
        .rank-row{ display:grid; grid-template-columns: 56px 1fr 1.4fr 64px; align-items:center; gap:8px; padding:8px 10px; border-radius:12px; border:1px solid rgba(212,175,55,0.25); }
        .rank-num{ font-weight:800; color:#d4af37; }
        .rank-ward{ font-weight:700; }
        .rank-score{ font-weight:800; text-align:right; }
        .kpi-card.active {
          border: 2px solid #d4af37 !important;
          box-shadow: 0 10px 26px rgba(212,175,55,.25), 0 6px 18px rgba(0,0,0,.28) !important;
          position: relative;
        }
        .kpi-card .active-ribbon {
          position: absolute;
          top: 8px;
          right: 10px;
          font-size: 11px;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 999px;
          background: linear-gradient(180deg, #ffe066, #d4af37);
          color: #111;
          border: 1px solid rgba(0,0,0,0.2);
        }
      `}</style>
      <style jsx>{`
        /* Insights collapse - transparent background */
        .insights-collapse,
        .insights-collapse .ant-collapse,
        .insights-collapse .ant-collapse-item,
        .insights-collapse .ant-collapse-header,
        .insights-collapse .ant-collapse-content,
        .insights-collapse .ant-collapse-content > .ant-collapse-content-box {
          background: transparent !important;
          box-shadow: none !important;
        }
        .insights-collapse .ant-collapse-item {
          border: 1px solid rgba(212,175,55,0.35) !important;
          border-radius: 14px !important;
        }
        .insights-collapse .ant-collapse-header {
          border-bottom: 1px solid rgba(212,175,55,0.22) !important;
        }
      `}</style>
      <div
        className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
        style={{
          background: isDark ? "#000" : "#fff",
          minHeight: "100vh",
          marginLeft: "var(--sidenav-width, 88px)",
          padding: "0 20px 0 12px",
          position: "relative",
          zIndex: 1002
        }}
      >
        <Row gutter={0}>
          <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24 luxe-dark-bg"
              style={{ margin: 0, position: "relative", zIndex: 1003 }}
              title={<span className="luxe-card-title">Indigent Data (Last 1000 Records to prioritize performance)</span>}
              extra={
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <Radio.Group onChange={onChange} defaultValue="All" className="luxe-radio">
                    <Radio.Button value="All">All</Radio.Button>
                    <Radio.Button value="Male">Male</Radio.Button>
                    <Radio.Button value="Female">Female</Radio.Button>
                  </Radio.Group>
                  <div className="mode-toggle">
                    <Segmented
                      options={[
                        { label: 'Cards', value: 'cards' },
                        { label: 'Table', value: 'table' }
                      ]}
                      value={viewMode}
                      onChange={(v) => setViewMode(v)}
                      defaultValue="cards"
                    />
                  </div>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8, paddingLeft:4 }}>
                    <span style={{ color: useDarkStyles ? '#fff8dc' : '#111', fontWeight: 600 }}>Dark</span>
                    <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                  </div>
                </div>
              }
              bodyStyle={{ background: "#000", borderRadius: "18px" }}
            >
              <Button
                type="primary"
                onClick={handleAddClick}
                className="luxe-btn"
                style={{
                  marginRight: 12,
                  fontSize: 16,
                  padding: "0 24px",
                  boxShadow: "0 0 16px 0 #d4af37cc",
                  position: "relative",
                  zIndex: 1004
                }}
              >
                New Verification
              </Button>

              <Button
                type="primary"
                onClick={handleExportToExcel}
                className="luxe-btn"
                style={{
                  marginRight: 12,
                  fontSize: 16,
                  padding: "0 24px",
                  boxShadow: "0 0 16px 0 #d4af37cc",
                  position: "relative",
                  zIndex: 1004
                }}
              >
                Export to Excel
              </Button>

              <Search
                placeholder="Search by ID or Full Name"
                allowClear
                size="large"
                enterButton={<Button className="luxe-search-btn" icon={<SearchOutlined />} />}
                onSearch={handleSearch}
                style={{
                  width: 420,
                  marginLeft: 16
                }}
                className="luxe-search"
              />

                <InsightsPanel
                  isDark={useDarkStyles}
                showInsights={showInsights}
                setShowInsights={setShowInsights}
                insightFilter={insightFilter}
                tileFilterLabel={tileFilterLabel}
                handleInsightTileClick={handleInsightTileClick}
                isTileActive={isTileActive}
                filteredDataCount={filteredData.length}
                totalRecords={totalRecords}
                uniqueWards={uniqueWards}
                wardNAPercent={wardNAPercent}
                alivePercent={alivePercent}
                marriedPercent={marriedPercent}
                femalePercent={femalePercent}
                hasDirectorshipYes={hasDirectorshipYes}
                mostIndigentWard={mostIndigentWard}
                mostIndigentWardCount={mostIndigentWardCount}
                mostIndigentWardPct={mostIndigentWardPct}
                leastIndigentWard={leastIndigentWard}
                leastIndigentWardCount={leastIndigentWardCount}
                leastIndigentWardPct={leastIndigentWardPct}
                topDeceased={topDeceased}
                VGI={VGI}
                nextBest={nextBest}
                nextBestPctInWard={nextBestPctInWard}
                pdAlertPct={pdAlertPct}
                directorshipExposurePct={directorshipExposurePct}
                equityBalance={equityBalance}
                leaderboardProps={leaderboardProps}
              />
              {viewMode === "cards" ? (
                <CardsView
                  cardSort={cardSort}
                  setCardSort={setCardSort}
                  cardFilters={cardFilters}
                  toggleCardFilter={toggleCardFilter}
                  clearAllCardFilters={clearAllCardFilters}
                  cardSortedData={cardSortedData}
                  getHasDirectorship={getHasDirectorship}
                  handleRowClick={handleRowClick}
                  handleExportToExcel={handleExportToExcel}
                />
              ) : (
                <TableView
                  hasTableFilters={hasTableFilters}
                  renderTableFilterChips={renderTableFilterChips}
                  clearTableFilters={clearTableFilters}
                  columns={columns}
                  filteredData={filteredData}
                  loading={loading}
                  tablePagination={tablePagination}
                  handleTableChange={handleTableChange}
                  handleRowClick={handleRowClick}
                  isDark={useDarkStyles}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>

      {/* Popup component */}
      {selectedPerson && (
        <Popup
          visible={isModalVisible}
          onClose={handleCloseModal}
          personData={selectedPerson}
        />
      )}
    </>
  );
}
